// The sales manager's navigator: they pilot the tour. The story on the left, what the customer sees in the middle with
// the tools and talking points, and a panel only the sales manager sees — the customer's brief, their requests, what
// they are looking at — with live assist floating over the stage when the meeting has it. Every move is sent as a
// few bytes of state; the customer's phone draws it.
import { LocalVideoTrack, RoomEvent, ScreenSharePresets, Track } from "livekit-client";
import { keepInk, keepStill, moderate, postEvents, readKept, readStill, siteLink } from "./api.js";
import { ASSIST_TOPIC, assistPanel } from "./assist.js";
import { pickShare, SHARE_TAB } from "./share.js";
import { firstName, h, initials, menuItem, showMenu, toast } from "./dom.js";
import { icon } from "./icons.js";
import { clock } from "./format.js";
import { applyView, canvasHTML, docAnchor, docSpot, fitPlans, isBoard, isPage, isStill, paintPointer, paintYaw, roomOf, sceneName, sceneOf, spotsOf, thumbOf, variantOf, openSlots } from "./canvas/render.js";
import { decode, makeSender, makeTextSender, roleOf, throttle, TOPIC } from "./canvas/sync.js";
import { mountPano, panoIn } from "./canvas/pano.js";
import { isSite, rmHosted, stillGoesTo } from "./canvas/privacy.js";
import { meetingWords } from "./host-words.js";
import { isSiteViaCrm, SITE_VIA_CRM, SITE_VIA_CRM_NEW, siteBoxMode, siteViaCrm } from "./site-via.js";
import { allowedDeletes, cleanStroke, colourOf, InkBook, inkId, LIMITS, mayDraw, nearestStroke, pageKey, pageOk, paintDocPeers, paintInk, paintPeers, peerFrom, pointerPage, readCapped, spotNear, unitsAt } from "./canvas/ink.js";

export function runNavigator(root, { code, info, pass, canvas, session }) {
  const { room } = session;
  const send = makeSender(room);
  const sendText = makeTextSender(room);
  // His talking points come with his own join, never with the canvas every participant downloads (audit F14).
  for (const sc of canvas.scenes) {
    const n = pass.notes?.scenes?.[sc.id];
    if (!n) continue;
    if (n.notes) sc.notes = n.notes;
    if (n.interim) sc.interim = n.interim;
    for (const [v, d] of Object.entries(n.variants || {})) if (sc.variants?.[v]) sc.variants[v].notes = d.notes;
  }
  const scenes = canvas.scenes;
  const firstPlan = scenes.find((s) => s.kind === "plan");
  const firstTour = scenes.find((s) => s.kind === "tour");
  const st = {
    scene: scenes[0].id, variant: firstPlan ? Object.keys(firstPlan.variants)[0] : null, spot: null, zoom: 1,
    room: firstTour ? firstTour.rooms[0].id : null, yaw: 40, pitch: 0, hear: false, ptr: null,
    cust: new Map(), requests: [], signals: [], dwell: {}, watch: null, shareTrack: null, startedAt: Date.now(), key: "",
    tool: "pointer", live: null, lasers: [], cleared: null, boards: 0, sz: 1, shareDims: null,
    holders: new Set(), others: new Map(), peers: new Map(),
    cam: null, glassOn: null, siteUrl: "", siteBusy: false, // the site camera (stage 11): on the stage, the track shown, its link, an ask in flight
    siteAsked: new Set(), siteMuted: false, // phones he has been asked about; his laptop's microphone turned off for his own phone
  };
  // His ink: every page's marks, the truth for every other screen. It survives his reload in this tab, as the tour does.
  const book = new InkBook();
  canvas.stills = canvas.stills || new Map(); // stills frozen from a shared tab (W3): n → { src, w, h, file }
  // This tab's copy of the book, and the studio's rev it was last in step with, kept together in one item: a book and a
  // rev saved apart could pair an old book with a new rev, and the old book would then be written back (W4 audit).
  const INK_SAVED = `ms-ink:v2:${code}`;
  let hadLocal = false; // this tab kept a book of its own (a reload), as against a new tab or another laptop (W4)
  let rev = 0;          // the studio's rev this tab last wrote or read (migrations 0008, 0009)
  try {
    const was = JSON.parse(sessionStorage.getItem(INK_SAVED) || "null");
    if (was && typeof was === "object") { hadLocal = true; rev = Number.isInteger(was.rev) ? was.rev : 0; book.load(was.book); }
  } catch { /* a private window */ }
  book.clear("share"); // marks on a shared tab's glass belong to that share only — never brought back (audit, P1)
  const pagesNow = () => new Map([...book.pages].filter(([p]) => p !== "share").map(([p, l]) => [p, JSON.stringify(l)]));
  const startBook = pagesNow(); // what this tab held as it started: the common ground for the first merge
  const tabCopy = () => { const snap = book.snapshot(); delete snap.share; return JSON.stringify({ rev, book: snap }); };
  // The ink comes first: if the store is full, the kept stills give way so the marks still fit (audit, P2).
  const keepInTab = throttle(() => {
    try { sessionStorage.setItem(INK_SAVED, tabCopy()); }
    catch { try { sessionStorage.removeItem(`ms-stills:${code}`); sessionStorage.setItem(INK_SAVED, tabCopy()); } catch { /* a nicety */ } }
  }, 400);
  // Every change is kept twice: in this tab at once, and in the studio a moment later (W4), so a new tab or another
  // laptop opens with every page. Only pages that changed since the last write go, each whole — at most 20 a call and
  // about 1.5 MB. A write names the rev it was made from; the studio refuses one that is behind (409), and this tab then
  // reads the studio's book and merges before it writes again.
  let kept = new Map(); // page → the JSON the studio holds for it, as far as this tab knows
  const refused = new Map(); // page → the JSON the studio refused (a 4xx): not sent again until it changes, and never
                             // mistaken for what the studio holds — a merge must not take those marks off (re-audit N3)
  let storing = false, again = false, retry = 0, finalAsked = false, warned = false;
  let restored = false; // nothing is written until the studio's book has been read once: a page written blind would
                        // take marks off it that this tab has never seen
  let stopped = false;  // this meeting's navigator has ended: a timer of its own must not write its book later
  const WAIT = new Set([408, 429]); // "not now" — the rate limit, a timeout: waited out and tried again, never final
  async function storeInk(final = false) {
    if (stopped && !final) return;
    if (storing || !restored) { again = true; if (final) finalAsked = true; return; }
    const now = pagesNow();
    const changed = [...now.keys()].filter((p) => kept.get(p) !== now.get(p) && refused.get(p) !== now.get(p))
      .concat([...kept.keys()].filter((p) => !now.has(p)));
    if (!changed.length) return;
    const batch = [];
    let bytes = 0;
    for (const p of changed) {
      const b = (now.get(p) || "[]").length;
      if (batch.length && (batch.length >= 20 || bytes + b > 1_500_000)) break;
      batch.push(p); bytes += b;
    }
    storing = true; again = false;
    try {
      const out = await keepInk(code, Object.fromEntries(batch.map((p) => [p, now.has(p) ? JSON.parse(now.get(p)) : []])), rev,
        { keepalive: final && bytes < 60_000 });
      for (const p of batch) { refused.delete(p); if (now.has(p)) kept.set(p, now.get(p)); else kept.delete(p); }
      rev = out.rev;
      keepInTab();
      retry = 0;
    } catch (err) {
      if (err.status === 409) {
        // The studio moved on — another laptop wrote. A screen that has ended lets its last word go (it is the older);
        // the screen he is on reads the studio's book, merges, and writes what is still its own.
        if (!stopped) { storing = false; await catchUp(); return; }
      } else if (err.status >= 400 && err.status < 500 && !WAIT.has(err.status)) {
        // Refused whole: not retried until those pages change again. The marks stay on every screen; he is told once.
        for (const p of batch) refused.set(p, now.get(p) || "[]");
        if (!warned) { warned = true; toast("Some marks could not be kept in the studio — they stay on every screen for this meeting.", 6000); }
      } else if (!stopped) {
        // Offline, or the studio busy: this tab still holds every mark; try again, a little later each time.
        retry = Math.min(60_000, (retry || 2500) * 2);
        setTimeout(() => storeSoon(), retry);
      }
    }
    storing = false;
    if (finalAsked) { finalAsked = false; storeInk(true); return; }
    if (!stopped && (again || changed.length > batch.length)) storeSoon();
  }
  const storeSoon = throttle(() => { storeInk(); }, 700);
  const saveInk = () => { keepInTab(); storeSoon(); };
  // Three ways, a page at a time: the common ground (what both last agreed on), the studio's book, and this tab's. A mark
  // this tab added since the common ground is kept; one it took off stays off; everything else is the studio's.
  function mergeInto(got, ground) {
    const theirs = got.pages || {};
    const all = new Set([...Object.keys(theirs), ...pagesNow().keys(), ...ground.keys()]);
    let moved = false;
    for (const p of all) {
      const base = JSON.parse(ground.get(p) || "[]"), mine = book.get(p), their = Array.isArray(theirs[p]) ? theirs[p] : [];
      const baseIds = new Set(base.map((s) => s.id)), mineIds = new Set(mine.map((s) => s.id));
      const added = mine.filter((s) => !baseIds.has(s.id));
      const taken = new Set(base.filter((s) => !mineIds.has(s.id)).map((s) => s.id));
      const theirIds = new Set(their.map((s) => s.id));
      const next = [...their.filter((s) => !taken.has(s.id)), ...added.filter((s) => !theirIds.has(s.id))];
      if (JSON.stringify(next) === JSON.stringify(mine)) continue;
      book.pages.delete(p);
      for (const s of next) book.add(p, s);
      moved = true;
    }
    kept = new Map(Object.entries(theirs).map(([p, l]) => [p, JSON.stringify(l)]));
    rev = got.rev || 0;
    keepInTab();
    return moved;
  }
  async function catchUp() {
    let got;
    try { got = await readKept(code); } catch { if (!stopped) setTimeout(() => storeSoon(), 5000); return; }
    if (stopped) return;
    if (mergeInto(got, kept)) { paint(true); inkChanged(); sendInk({ snapshot: book.snapshot() }); }
    storeSoon();
  }
  // Where the tour was and what they asked survive the sales manager's reload or rejoin in this tab — without it the
  // customer was sent back to Welcome and his requests were gone (audit P1).
  const SAVED = `ms-nav:${code}`;
  try {
    const was = JSON.parse(sessionStorage.getItem(SAVED) || "null");
    if (was && (canvas.byId[was.scene] || isPage(was.scene))) {
      for (const k of ["scene", "variant", "spot", "zoom", "room", "yaw", "pitch", "boards", "stillsN", "siteUrl"]) if (was[k] !== undefined) st[k] = was[k];
      if (typeof st.siteUrl !== "string" || !/^https?:\/\/[^\s/]+\/m\/[A-Za-z0-9-]{6,20}$/.test(st.siteUrl)) st.siteUrl = ""; // only a link of ours
      st.requests = Array.isArray(was.requests) ? was.requests : [];
    }
  } catch { /* a private window */ }
  const save = throttle(() => {
    try {
      sessionStorage.setItem(SAVED, JSON.stringify({ scene: st.scene, variant: st.variant, spot: st.spot, zoom: st.zoom, room: st.room, yaw: st.yaw, pitch: st.pitch, boards: st.boards, stillsN: st.stillsN, siteUrl: st.siteUrl,
        requests: st.requests.map((r) => ({ ...r, fresh: false })) }));
    } catch { /* a nicety */ }
  }, 400);
  const slots = openSlots(info.meeting.startsAt);
  const brief = pass.brief || {};
  // A film's place is read from the film itself, so every message — the heartbeat too — carries where it really is.
  const hostVideo = () => (sceneOf(canvas, st.scene).kind === "video" ? navCanvas.querySelector(".cv-video") : null);
  // While he shares a tab the film's element is gone from his stage; its place is kept, paused, so her film does not
  // jump back to 0:00 and his own picks up where it was when the share stops (audit, P2).
  const liveVid = () => {
    const el = hostVideo();
    const kept = st.lastVid && st.lastVid.scene === st.scene ? st.lastVid : null;
    // A film just rebuilt (after a share stops) is at 0:00 until it loads and is sent back to its place: until then
    // the kept place stands, or the rebuild would overwrite it with 0 (re-audit F5).
    if (el && el.readyState < 1 && kept) return { on: false, t: kept.t };
    if (el) { st.lastVid = { scene: st.scene, on: !el.paused && !el.ended, t: Math.round(el.currentTime * 100) / 100 }; return { on: st.lastVid.on, t: st.lastVid.t }; }
    // No film on his stage — a tab shared, or the share just stopped and the stage not yet rebuilt — keeps the place
    // too: in that gap her film would otherwise flash to 0:00 (re-audit round 2, Low).
    return kept ? { on: false, t: kept.t } : null;
  };
  // The glass (W3; stage 11): a live picture on the stage — his shared tab, or the site camera he has put there. One at
  // a time. Every screen draws, points and zooms on it the same way, as the page "share".
  const onGlass = () => Boolean(st.shareTrack || st.cam);
  const camOf = (identity) => {
    const pub = identity ? room.remoteParticipants.get(identity)?.getTrackPublication(Track.Source.Camera) : null;
    return pub && !pub.isMuted ? pub.track || null : null;
  };
  const glassTrack = () => st.shareTrack || camOf(st.cam);
  // sz: his zoom on the glass (W3), which every screen follows. cam: the site camera on the stage, by its identity —
  // every phone shows that participant's camera only when the identity is a site camera's.
  const view = () => ({ scene: st.scene, variant: st.variant, spot: st.spot, zoom: st.zoom, room: st.room, yaw: st.yaw, pitch: st.pitch, vid: liveVid(), sz: onGlass() ? st.sz : 1, cam: st.cam });

  // ------------------------------------------------------------------------------------------ the frame --
  const who = h("span", { class: "who" });
  const timer = h("small", { class: "tm", text: "00:00" });
  const pill = h("span", { class: "pill" });
  const endBtn = h("button", { class: "n-end", type: "button", text: "End", "aria-haspopup": "menu", onclick: () => endMenu() });
  // Ravi, 19 Sep: "allow the left panel (story) and the right panel … to be minimized to give more space". The story
  // folds to its pictures (a scene is still one click away); the private panel folds away. Remembered on this laptop.
  const railBtn = h("button", { class: "n-fold", type: "button", onclick: () => layout({ rail: !lay.rail }) });
  const sideBtn = h("button", { class: "n-fold", type: "button", onclick: () => layout({ side: !lay.side }) });
  const top = h("header", { class: "n-top" }, railBtn, h("span", { class: "brand canvas-brand" }, h("b", { text: "Walkin" }), " · the navigator"), who, timer, h("span", { class: "sp" }), pill, sideBtn, endBtn);
  const rail = h("nav", { class: "n-rail", "aria-label": "The story" });
  const lab = h("div", { class: "lab" });
  const navCanvas = h("div", { class: "n-canvas", id: "navCanvas" });
  const tools = h("div", { class: "tools" });
  const inkTools = h("div", { class: "tools ink-tools", hidden: true });
  const notes = h("div", { class: "notes" });
  const ptrNote = h("div", { class: "ptr-note", hidden: true });
  const siteAsk = h("div", { class: "site-ask", hidden: true, role: "alertdialog", "aria-label": "The site camera is live" });
  const stageEl = h("section", { class: "n-stage" }, lab, navCanvas, tools, inkTools, notes, ptrNote, siteAsk);
  const penBox = h("div", { class: "penbox" });
  const siteBox = h("div", { class: "sitebox" });
  const reqBox = h("div");
  const sigBox = h("ul", { class: "sig" });
  const side = h("aside", { class: "n-side", "aria-label": "Only you see this" },
    h("div", { class: "private", text: "🔒 Only you see this" }),
    h("div", { class: "body" }, briefEl(), h("h6", { text: "The pen" }), penBox, h("h6", { text: "Site camera" }), siteBox, h("h6", { text: "Requests" }), reqBox, h("h6", { text: "What they are looking at" }), sigBox));
  const tiles = h("div", { class: "tiles" });
  const micBtn = h("button", { class: "n-ctl", type: "button", onclick: () => toggleMic() });
  const camBtn = h("button", { class: "n-ctl", type: "button", onclick: () => toggleCam() });
  const shareBtn = h("button", { class: "n-ctl wide", type: "button", onclick: () => toggleShare() });
  const bottom = h("footer", { class: "n-bottom" }, tiles, h("span", { class: "sp" }),
    h("span", { class: "hint", text: "No screen share needed — the canvas is drawn on their phone" }), micBtn, camBtn, shareBtn);
  const shell = h("div", { class: "nv" }, top, rail, stageEl, side, bottom);
  const assist = info.meeting.assist ? assistPanel({
    names: () => ({ customer: firstName(primary()?.name) }),
    onShow: (a) => show(a),
    isShowing: (a) => a.scene === st.scene && (!a.variant || a.variant === st.variant) && (!a.spot || a.spot === st.spot),
  }) : null;
  if (assist) shell.append(assist.el);
  document.body.classList.add("ms-dark");
  root.replaceChildren(shell);
  const LAYOUT = "ms-nv-layout";
  let lay = { rail: false, side: false };
  try { lay = { ...lay, ...JSON.parse(localStorage.getItem(LAYOUT) || "{}") }; } catch { /* a nicety */ }
  function layout(patch = {}) {
    lay = { ...lay, ...patch };
    shell.classList.toggle("rail-min", lay.rail);
    shell.classList.toggle("side-min", lay.side);
    railBtn.textContent = lay.rail ? "Story ›" : "‹ Story";
    railBtn.setAttribute("aria-expanded", String(!lay.rail));
    railBtn.setAttribute("aria-label", lay.rail ? "Open the story" : "Fold the story to its pictures");
    sideBtn.textContent = lay.side ? "‹ Private panel" : "Private panel ›";
    sideBtn.setAttribute("aria-expanded", String(!lay.side));
    sideBtn.setAttribute("aria-label", lay.side ? "Open the private panel" : "Fold the private panel away");
    try { localStorage.setItem(LAYOUT, JSON.stringify(lay)); } catch { /* a nicety */ }
    if (Object.keys(patch).length) requestAnimationFrame(() => { fitPlans(navCanvas); paint(); });
  }
  layout();

  function briefEl() {
    // CR-20261009-001 L3.1: on an RM-hosted meeting the viewer IS the RM — the RM rows go (host-words.js)
    const rows = meetingWords(info.meeting).briefRows([["From", brief.source], ["Family", brief.family], ["Wants", brief.wants], ["Asked RM", brief.asked], ["RM", brief.rm]].filter(([, v]) => v));
    return h("div", {}, h("h6", { text: "Brief" }), rows.length
      ? h("dl", { class: "brief" }, rows.map(([k, v]) => h("div", {}, h("dt", { text: k }), h("dd", { text: v }))))
      : h("p", { class: "assist", text: "No brief came with this meeting." }));
  }

  // ------------------------------------------------------------------------------------------ painting --
  function primary() {
    const people = [...room.remoteParticipants.values()];
    return people.find((p) => roleOf(p) === "customer") || people.find((p) => roleOf(p) === "guest") || null;
  }
  function paint(force = false) {
    const v = view();
    const sc = sceneOf(canvas, v.scene);
    // While he shares a tab, his stage is that tab — what she sees — with the same ink layer as a page (W3, D92 A).
    if (onGlass()) {
      if (st.key !== "share" || force || glassTrack() !== st.glassOn) renderShare();
      paintHostInk();
      paintRail();
      paintTools(sc);
      paintStatus();
      assist?.refresh();
      return;
    }
    const key = [v.scene, sc.kind === "plan" ? v.variant : "", roomOf(sc, v)?.id || ""].join("|");
    // What plays stays put while its scene does — the 360° viewer, the film, a walk-through someone else hosts: a
    // repaint (someone joining, a reconnect) would reload every panorama or send the film back to 0:00 (audit F9).
    const keep = ["pano", "video", "embed"].includes(sc.kind) && key === st.key && navCanvas.querySelector(".pano360, .cv-video, .embed-frame");
    if ((force || key !== st.key) && !keep) {
      panoIn(navCanvas)?.destroy();
      navCanvas.innerHTML = canvasHTML(canvas, v, { who: "nav", lang: "en", first: firstName(primary()?.name || ""), hostFirst: firstName(info.meeting.hostName), slots, unit: sc.kind === "plan" ? variantOf(sc, v).name : "home" });
      st.key = key;
      fitPlans(navCanvas);
      const film = navCanvas.querySelector(".cv-video");
      if (film) {
        playFor(film);
        const back = st.lastVid && st.lastVid.scene === st.scene ? st.lastVid.t : 0;
        if (back > 0) film.addEventListener("loadedmetadata", () => { film.currentTime = back; }, { once: true });
      }
      const box = navCanvas.querySelector(".pano360");
      if (box) mountPano(box, sc.pano, canvas.base, { who: "nav", hostFirst: firstName(info.meeting.hostName), onLook: (look) => { st.yaw = look.yaw; st.pitch = look.pitch; pushState(); }, onGo: (to) => walk(sc, to), onPoint: (uv) => { st.ptr = uv; pushPtr(uv); } });
    }
    applyView(navCanvas, canvas, v);
    paintPointer(navCanvas, st.ptr);
    paintHostInk();
    paintRail();
    paintTools(sc);
    paintStatus();
    assist?.refresh();
  }
  function paintRail() {
    rail.replaceChildren(h("h6", { text: "The story" }), ...scenes.map((s, i) => {
      const secs = st.dwell[s.id] || 0;
      return h("button", { class: "scn" + (s.id === st.scene ? " on" : ""), type: "button", onclick: () => go(s.id), title: `${i + 1}. ${s.name.en}` },
        h("span", { class: "th", style: `background-image:url('${thumbOf(canvas, s, { variant: st.variant, room: st.room })}')` }),
        h("span", { class: "tx" }, h("b", { text: s.name.en }), h("small", { text: secs ? `${clock(secs)} with them` : "not shown yet" })));
    }), h("p", { class: "keys", text: "← → move through the story" }), ...pagesRail());
  }
  // Boards (W3): pages of paper he adds during the meeting, each with its own ink.
  function pagesRail() {
    const n = Math.max(st.boards, ...[...book.pages.keys()].filter(isBoard).map((k) => +k.split(":")[1]), 0);
    st.boards = n;
    const item = (id) => {
      const sc = sceneOf(canvas, id), marks = book.count(id), secs = st.dwell[id] || 0;
      return h("button", { class: "scn" + (id === st.scene ? " on" : ""), type: "button", onclick: () => go(id), title: sc.name.en },
        h("span", { class: "th paper-th" }),
        h("span", { class: "tx" }, h("b", { text: sc.name.en }), h("small", { text: `${marks ? `${marks} mark${marks === 1 ? "" : "s"}` : "blank"}${secs ? ` · ${clock(secs)} with them` : ""}` })));
    };
    const stills = [...canvas.stills.keys()].sort((a, b) => a - b).map((k) => {
      const id = `still:${k}`, marks = book.count(id);
      return h("button", { class: "scn" + (id === st.scene && !onGlass() ? " on" : ""), type: "button", onclick: () => stillGo(id), title: sceneOf(canvas, id).name.en },
        h("span", { class: "th", style: `background-image:url('${canvas.stills.get(k).src}')` }),
        h("span", { class: "tx" }, h("b", { text: sceneOf(canvas, id).name.en }), h("small", { text: marks ? `${marks} mark${marks === 1 ? "" : "s"}` : "no marks" })));
    });
    return [h("h6", { class: "pages-h", text: stills.length ? "Boards and stills" : "Boards" }), ...Array.from({ length: n }, (_, i) => item(`board:${i + 1}`)), ...stills,
      h("button", { class: "addboard", type: "button", text: "+ A blank board", onclick: () => addBoard() })];
  }
  // A still is shown when he is not sharing; while he shares, the tab is what she sees.
  function stillGo(id) {
    if (onGlass()) { toast(st.cam ? "Go back to the story to show a still — they are watching the site camera." : "Stop sharing to show a still — while you share, they see the tab."); return; }
    go(id);
  }
  function addBoard() {
    if (st.boards >= 20) { toast("Twenty boards is the most in one meeting."); return; }
    st.boards += 1;
    go(`board:${st.boards}`);
  }
  function tool(text, on, onClick, attrs = {}) {
    return h("button", { class: "tool" + (on ? " on" : ""), type: "button", text, onclick: onClick, ...attrs });
  }
  function paintTools(sc) {
    const v = view();
    if (onGlass()) {
      const cam = st.cam && room.remoteParticipants.get(st.cam);
      tools.replaceChildren(h("span", { class: "k", text: cam ? "Site" : "Tab" }),
        tool("−", false, () => shareZoom(-0.4), { "aria-label": "Zoom out" }), tool("+", false, () => shareZoom(0.4), { "aria-label": "Zoom in" }), tool("Fit", false, () => shareZoom(0)),
        h("span", { class: "sep" }),
        tool("❄ Freeze as a page", false, () => freeze(), { title: "Keep this frame as a still in your story, with its marks" }),
        cam ? tool("Back to the story", false, () => showSite(false)) : tool("Stop sharing", false, () => toggleShare()));
      paintInkTools();
      notes.replaceChildren();
      lab.replaceChildren(h("span", { text: `WHAT ${(firstName(primary()?.name) || "THEY").toUpperCase()} SEES — ${cam ? `${firstName(cam.name) || "the site"}'s camera at the site, live` : "the tab you are sharing"}` }),
        h("span", { text: `Marks on a live ${cam ? "picture" : "tab"} stay on the glass · Freeze as a page to keep one` }));
      return;
    }
    const parts = pageKey(sc, v) ? [] : [h("span", { class: "tool on static", text: "● Pointer" })];
    if (sc.kind === "plan") {
      parts.push(h("span", { class: "sep" }), ...Object.entries(sc.variants).map(([k, vv]) => tool(vv.short || vv.name, st.variant === k, () => set({ variant: k, spot: null, zoom: 1 }))));
    }
    const spots = spotsOf(sc, v);
    if (spots.length && sc.kind !== "tour") parts.push(h("span", { class: "sep" }), ...spots.map((p) => tool(p.label.split(" · ")[0], st.spot === p.id, () => spotlight(p))));
    if (["plan", "fit", "board", "still", "pano"].includes(sc.kind)) parts.push(h("span", { class: "sep" }), tool("−", false, () => set({ zoom: Math.max(1, st.zoom - 0.4) }), { "aria-label": "Zoom out" }), tool("+", false, () => set({ zoom: Math.min(3, st.zoom + 0.4) }), { "aria-label": "Zoom in" }), tool("Fit", false, () => set({ zoom: 1, spot: null })));
    if (sc.kind === "tour") {
      parts.push(h("span", { class: "sep" }), ...sc.rooms.map((r) => tool(r.label, st.room === r.id, () => { set({ room: r.id, yaw: 40 }); signal(`Walked into the ${r.label.toLowerCase()}`); })),
        h("span", { class: "sep" }), tool("◀ Turn", false, () => set({ yaw: Math.max(0, st.yaw - 15) })), tool("Turn ▶", false, () => set({ yaw: Math.min(100, st.yaw + 15) })));
    }
    if (sc.kind === "video") {
      parts.push(h("span", { class: "sep" }), tool(st.hear ? "🔊 You hear it too" : "🔇 Only they hear it", st.hear, () => { st.hear = !st.hear; const el = hostVideo(); if (el) el.muted = !st.hear; paintTools(sc); }, { title: "Turn yours on only with headphones — your microphone would send it back to them" }));
    }
    if (sc.kind === "pano" && sc.pano) {
      const here = sc.pano.frames[st.room]?.room;
      parts.push(h("span", { class: "sep" }), ...(sc.rooms || []).filter((r) => sc.pano.frames[r.frame]).map((r) => tool(r.label, here === sc.pano.frames[r.frame].room, () => walk(sc, r.frame))));
    }
    // Someone else's walk-through: the room buttons take them with you; inside it, each looks around on their own.
    if (sc.kind === "embed") {
      parts.push(h("span", { class: "sep" }), ...sc.rooms.map((r) => tool(r.label, roomOf(sc, v).id === r.id, () => { set({ room: r.id }); signal(`Took them into the ${r.label.toLowerCase()}`); })));
    }
    while (parts[0]?.classList?.contains("sep")) parts.shift();
    tools.replaceChildren(...parts);
    paintInkTools();
    const variant = sc.kind === "plan" ? variantOf(sc, v) : null;
    const lines = [...(variant?.notes || []), ...(sc.notes || [])];
    notes.replaceChildren(...(lines.length ? [h("b", { text: "Say · " }), lines.join("  ·  ")] : []));
    if (sc.interim) notes.append(h("em", { text: `  (${sc.interim})` }));
    const cust = primary();
    const cs = cust && st.cust.get(cust.identity);
    lab.replaceChildren(h("span", { text: `WHAT ${(firstName(cust?.name) || "THEY").toUpperCase()} SEES${cs && cs.face ? " — when they return to the tour (they have your video full screen)" : cs && !cs.follow ? " — when they follow you" : ""}` }),
      h("span", { text: `${sc.name.en}${variant ? " · " + variant.name : ""}${roomOf(sc, v) ? " · " + roomOf(sc, v).label : ""}${sc.kind === "pano" && sc.pano?.frames[v.room]?.room ? " · " + sc.pano.frames[v.room].room : ""}` }));
  }
  function paintStatus() {
    const cust = primary();
    const cs = cust && st.cust.get(cust.identity);
    const name = firstName(cust?.name) || "Your customer";
    const custNames = [...room.remoteParticipants.values()].filter((p) => !["host", "site"].includes(roleOf(p))).map((p) => p.name).filter(Boolean);
    who.replaceChildren(document.createTextNode(custNames.length ? custNames.join(", ") : "Waiting for your customer"), h("small", { text: `${canvas.project} · video call` }));
    pill.className = "pill";
    if (!cust) { pill.classList.add("warn"); pill.replaceChildren(h("i"), "Waiting for them to join"); }
    else if (st.shareTrack) { pill.classList.add("warn"); pill.replaceChildren(h("i"), "You are sharing a tab", h("button", { type: "button", text: "Stop", onclick: () => toggleShare() })); }
    else if (st.cam) { pill.classList.add("warn"); pill.replaceChildren(h("i"), "You are showing the site camera", h("button", { type: "button", text: "Stop", onclick: () => showSite(false) })); }
    else if (cs && cs.face) { pill.classList.add("warn"); pill.replaceChildren(h("i"), `${name} is watching you full screen`, h("button", { type: "button", text: "Ask back to the tour", onclick: () => { send(TOPIC.ASK, { ask: "tour" }, { to: cust.identity }); toast("Asked"); } })); }
    else if (cs && !cs.follow) { pill.classList.add("warn"); pill.replaceChildren(h("i"), `${name} is exploring · ${sceneOf(canvas, cs.scene).name.en}`, h("button", { type: "button", text: "Ask to follow", onclick: () => { send(TOPIC.ASK, { ask: "follow" }, { to: cust.identity }); toast("Asked"); } })); }
    else pill.replaceChildren(h("i"), `${name} is following you`);
    const lp = room.localParticipant;
    micBtn.innerHTML = icon(lp.isMicrophoneEnabled ? "mic" : "micOff");
    micBtn.classList.toggle("off", !lp.isMicrophoneEnabled);
    micBtn.setAttribute("aria-label", lp.isMicrophoneEnabled ? "Mute" : "Unmute");
    camBtn.innerHTML = icon(lp.isCameraEnabled ? "video" : "videoOff");
    camBtn.classList.toggle("off", !lp.isCameraEnabled);
    camBtn.setAttribute("aria-label", lp.isCameraEnabled ? "Stop video" : "Start video");
    shareBtn.innerHTML = icon("share");
    shareBtn.append(document.createTextNode(st.shareTrack ? " Stop sharing" : " Share a tab"));
    shareBtn.hidden = !navigator.mediaDevices?.getDisplayMedia;
  }
  const tileEls = new Map();
  function paintTiles() {
    const people = [...room.remoteParticipants.values()].sort((a, b) => (roleOf(a) === "customer" ? -1 : 0) - (roleOf(b) === "customer" ? -1 : 0));
    people.push(room.localParticipant);
    const alive = new Set();
    tiles.replaceChildren(...people.map((p) => {
      alive.add(p.identity);
      let tEl = tileEls.get(p.identity);
      if (!tEl) {
        const video = h("video", { autoplay: true, playsinline: true, muted: true });
        tEl = { el: h("div", { class: "who-tile" }, video, h("div", { class: "face", text: initials(p.name || p.identity) }), h("span"), h("em")), video, track: null };
        tileEls.set(p.identity, tEl);
      }
      const pub = p.getTrackPublication(Track.Source.Camera);
      const track = pub && !pub.isMuted ? pub.track : null;
      if (track !== tEl.track) { tEl.track?.detach(tEl.video); if (track) track.attach(tEl.video); tEl.track = track; }
      tEl.el.classList.toggle("has-video", Boolean(track));
      tEl.el.classList.toggle("talk", p.isSpeaking);
      tEl.el.classList.toggle("self", p.isLocal);
      const role = roleOf(p);
      tEl.el.querySelector("span").textContent = p.isLocal ? "You" : `${firstName(p.name)} · ${role === "customer" ? "customer" : role === "rm" ? "RM" : role === "site" ? "site camera" : "guest"}${st.holders.has(p.identity) ? " · pen" : ""}${track ? "" : " · camera off"}`;
      const mic = p.getTrackPublication(Track.Source.Microphone);
      tEl.el.querySelector("em").innerHTML = mic && !mic.isMuted ? icon("mic") : icon("micOff");
      return tEl.el;
    }));
    for (const id of [...tileEls.keys()]) if (!alive.has(id)) { tileEls.get(id).track?.detach(tileEls.get(id).video); tileEls.delete(id); }
  }
  function paintRequests() {
    reqBox.replaceChildren(...(st.requests.length ? st.requests.slice().reverse().map((r) => {
      const actions = [];
      if (!r.done && r.kind === "cost") actions.push(h("button", { class: "btn-s gold", type: "button", text: "Mark as sent", onclick: () => ack(r, "sent") }));
      if (!r.done && r.kind === "ask") actions.push(h("button", { class: "btn-s ghost", type: "button", text: "Answered", onclick: () => ack(r, "answered") }));
      return h("div", { class: "req" + (r.done ? " done" : "") + (r.fresh ? " new" : "") },
        h("b", { text: r.title }), r.line ? h("span", { text: r.line }) : null,
        r.done ? h("div", { class: "row ok", text: `✓ ${r.done}` }) : actions.length ? h("div", { class: "row" }, actions) : null);
    }) : [h("p", { class: "assist", text: "Their taps on Book, Cost sheet and Ask appear here." })]));
  }
  function paintSignals() {
    const top = Object.entries(st.dwell).filter(([, s]) => s > 0).sort((a, b) => b[1] - a[1]).slice(0, 4)
      .map(([id, s]) => h("li", {}, h("span", { text: sceneOf(canvas, id).name.en }), h("span", { text: clock(s) })));
    const ev = st.signals.slice(-5).reverse().map((e) => h("li", { class: e.fresh ? "new" : "" }, h("span", { text: e.text }), h("span", { text: e.at })));
    sigBox.replaceChildren(...ev, ...top);
  }

  // ------------------------------------------------------------------------------------------ steering --
  // What is on the stage and when, for the recording's scenes.json (ms-record): the story's scene, his shared tab, or the
  // site camera. Said once it has settled — a second of clicking through scenes is one line, not five — and only when it
  // is something other than what was said last, so turning and zooming say nothing.
  let shown = "", shownTimer = null;
  function logShown() {
    clearTimeout(shownTimer);
    shownTimer = setTimeout(() => {
      if (stopped) return;
      const v = view();
      const sc = sceneOf(canvas, v.scene);
      const on = st.cam ? "site" : st.shareTrack ? "share" : "story";
      const e = on === "story"
        ? { t: "scene", kind: on, scene: v.scene, variant: sc.kind === "plan" ? v.variant || null : null, room: roomOf(sc, v)?.id || null,
            text: sceneName(canvas, v.scene) + (sc.kind === "plan" && variantOf(sc, v) ? ` · ${variantOf(sc, v).name}` : "") }
        : { t: "scene", kind: on, text: on === "site" ? firstName(room.remoteParticipants.get(st.cam)?.name) || "the site camera" : "a shared tab" };
      const key = JSON.stringify(e);
      if (key === shown) return;
      shown = key;
      postEvents(code, [e]);
    }, 900);
  }
  const pushState = throttle(() => send(TOPIC.STATE, view()), 60);
  // A new scene, unit, room or spotlight repaints the navigator; turning and zooming only move the canvas.
  function set(patch) {
    if ("scene" in patch && patch.scene !== st.scene) account();
    const full = ["scene", "variant", "room", "spot"].some((k) => k in patch && patch[k] !== st[k]);
    Object.assign(st, patch);
    pushState();
    save();
    if (full) { paint(); logShown(); }
    else applyView(navCanvas, canvas, view());
  }
  function go(id) {
    const sc = sceneOf(canvas, id);
    const pano = sc.kind === "pano" && sc.pano ? { room: sc.pano.frames[st.room] ? st.room : sc.pano.start, yaw: 0, pitch: 0 } : {};
    set({ scene: id, spot: null, zoom: 1, ...(sc.kind === "tour" ? { yaw: 40 } : {}), ...pano });
  }
  // The film: the sales manager's controls drive both screens. Their own copy is silent unless they turn it on —
  // their microphone would otherwise send it back to the customer, who then hears it twice.
  function playFor(el) {
    el.controls = true;
    el.muted = !st.hear;
    const now = () => { if (el.isConnected) send(TOPIC.STATE, view()); }; // a film no longer on the page says nothing
    for (const ev of ["play", "pause", "seeked", "ended"]) el.addEventListener(ev, now);
    el.addEventListener("play", () => { if (el.currentTime < 1) signal(`Played ${sceneOf(canvas, st.scene).name.en.toLowerCase()} for them`); });
    let last = 0;
    el.addEventListener("timeupdate", () => { if (performance.now() - last > 2000) { last = performance.now(); now(); } });
  }
  // A step in the 360° walk-through — an arrow, a dot on the plan or a room button. Named in the panel when the room changes.
  function walk(sc, to) {
    const was = sc.pano.frames[st.room]?.room;
    set({ room: to });
    const now = sc.pano.frames[to]?.room;
    if (now && now !== was) signal(`Walked into the ${now.toLowerCase()}`);
  }
  function spotlight(p) {
    const on = st.spot !== p.id;
    set({ spot: on ? p.id : null, zoom: on && sceneOf(canvas, st.scene).kind !== "grid" ? 1.9 : 1 });
  }
  // Where live assist says to go next: a scene, and perhaps a unit and a spot on it. Only what the canvas has.
  function show(a) {
    if (!a || !scenes.some((s) => s.id === a.scene)) return;
    const sc = sceneOf(canvas, a.scene);
    if (a.variant && !(sc.variants && sc.variants[a.variant])) return;
    set({ scene: a.scene, spot: null, zoom: 1, ...(a.variant ? { variant: a.variant } : {}), ...(sc.kind === "tour" ? { yaw: 40 } : {}) });
    const p = a.spot && spotsOf(sc, view()).find((x) => x.id === a.spot);
    if (p) spotlight(p);
  }
  function signal(text) {
    st.signals.push({ text, at: clock((Date.now() - st.startedAt) / 1000), fresh: true });
    setTimeout(() => { st.signals.forEach((e) => (e.fresh = false)); }, 1600);
    paintSignals();
  }
  function ack(r, done) {
    setTimeout(save, 0);
    r.done = done === "sent" ? "Marked as sent — WhatsApp sending comes with the Growin connection" : "Answered";
    send(TOPIC.ACK, { id: r.id, done }, { to: r.from });
    postEvents(code, [{ t: done, kind: r.kind, unit: r.unit, text: r.text }]);
    paintRequests();
  }

  // ------------------------------------------------------------------------------------------ ink --
  // His marks on the plans and maps (W1). A finished stroke goes to every screen as a text stream, the stroke being
  // drawn as a few lossy points at a time, undo / erase / clear as small reliable messages; a joiner gets the whole book.
  const me = () => room.localParticipant.identity;
  const INK_TOOLS = [["pointer", "● Pointer", "Esc"], ["pen", "✎ Pen", "P"], ["laser", "Laser", "L"], ["hl", "Highlight", "H"], ["arrow", "↗ Arrow", "A"], ["label", "T Label", "T"], ["eraser", "Eraser", "E"]];
  const curPage = () => (onGlass() ? "share" : pageKey(sceneOf(canvas, st.scene), view()));
  const sendInk = (body, to) => sendText(TOPIC.INK, body, to ? { to } : {});
  function paintHostInk() {
    const page = curPage();
    if (!page) return;
    const live = [...(st.live && st.live.page === page && st.live.tool !== "label" ? [st.live] : []), ...st.lasers.filter((l) => l.page === page),
      ...[...st.others.values()].filter((l) => l.page === page)];
    paintInk(navCanvas, book.get(page), live);
    paintPointers();
  }
  // Her pointer (W2): a dot where she presses and holds, and his panel's words for what it is on.
  // Where she points: on a page (percent), in the 360° (a direction), or on the price page (a place in a row) — W2, W3.
  function paintPointers() {
    const sc = sceneOf(canvas, st.scene);
    const page = onGlass() ? "share" : pointerPage(sc, view());
    const here = [...st.peers.values()].filter((p) => p.page === page).map((p) => ({ ...p, colour: colourOf(p.id) }));
    const pv = panoIn(navCanvas);
    if (pv) pv.peers(here);
    else if (sc.kind === "price" && !onGlass()) paintDocPeers(navCanvas, here, docSpot);
    else paintPeers(navCanvas, here);
    const now = here.find((p) => !p.gone && (!pv || !p.frame || p.frame === pv.view.frame));
    ptrNote.hidden = !now;
    if (now) {
      const on = Number.isFinite(now.x) ? spotNear(spotsOf(sc, view()), now.x, now.y)
        : Number.isFinite(now.row) ? docSpot(navCanvas.querySelector(".cv-doc"), now)?.text
        : sc.pano?.frames?.[now.frame]?.room;
      ptrNote.replaceChildren(h("i", { style: `background:${colourOf(now.id)}` }), `${now.name} is pointing${on ? ` · ${on}` : ""}`);
    }
  }
  function onPeerPointer(body, p) {
    const was = st.peers.get(p.identity);
    const next = peerFrom(body, p, firstName(p.name) || "Someone");
    if (next) {
      st.peers.set(p.identity, next);
    } else if (was) {
      was.gone = true;
      setTimeout(() => { if (st.peers.get(p.identity) === was && was.gone) { st.peers.delete(p.identity); paintPointers(); } }, 1400);
    }
    paintPointers();
  }

  // ------------------------------------------------------------------------------------------ pens --
  // He gives a pen to the customer or a family member and takes it back (D93 A). Every screen accepts a stroke only
  // from him or from someone his latest message names; when he leaves, every pen comes back.
  function givable() {
    return [...room.remoteParticipants.values()].filter((p) => ["customer", "guest"].includes(roleOf(p)));
  }
  function setHolders(next) {
    const present = new Set(givable().map((p) => p.identity));
    const before = st.holders;
    st.holders = new Set([...next].filter((id) => present.has(id)));
    const lost = [...before].filter((id) => !st.holders.has(id));
    send(TOPIC.PEN, { holders: [...st.holders] });
    // A pen taken back mid-stroke: that stroke goes now (her "cancel" arrives after she stopped being a holder).
    for (const [id, l] of st.others) if (!st.holders.has(l.by)) st.others.delete(id);
    // A stroke she finished just before the pen went back may have reached some phones and not his book: a moment
    // later every screen is given his book again, so all agree (audit, P2).
    for (const id of lost) setTimeout(() => sendInk({ owner: id, book: book.ownedBy(id) }), 1200);
    paintHostInk();
    paintPen();
    paintTiles();
  }
  function paintPen() {
    const people = givable();
    if (!people.length) { penBox.replaceChildren(h("p", { class: "assist", text: "When they join, you can give them a pen." })); return; }
    const with_ = people.filter((p) => st.holders.has(p.identity)).map((p) => firstName(p.name));
    penBox.replaceChildren(
      h("p", { class: "pen-line", text: with_.length ? `${with_.join(" and ")} ${with_.length === 1 ? "has" : "have"} a pen. Their marks are in their own colour.` : "Only you are drawing." }),
      h("div", { class: "row" }, ...people.map((p) => st.holders.has(p.identity)
        ? h("button", { class: "btn-s ghost", type: "button", text: `Take ${firstName(p.name)}'s pen back`, onclick: () => setHolders([...st.holders].filter((i) => i !== p.identity)) })
        : h("button", { class: "btn-s gold", type: "button", text: `Give ${firstName(p.name)} a pen`, onclick: () => setHolders([...st.holders, p.identity]) }))));
  }
  // ------------------------------------------------------------------------------------------ the site camera --
  // Stage 11, S1 (context/22-canvas-site-camera.md): someone at the property opens a link on their phone and films; he
  // puts that picture on every stage, where it is the glass — zoom, pointers, ink and Freeze, as for a shared tab.
  const sitePeople = () => [...room.remoteParticipants.values()].filter((p) => roleOf(p) === "site");
  async function askLink(fresh = false, { quiet = false } = {}) {
    if (st.siteBusy) return; // one ask at a time: two New links at once could leave him showing a withdrawn one
    // CR-20261009-001 L3.4: on an RM-hosted meeting only ClikWork (through Helix) makes or replaces the link
    if (fresh && siteViaCrm(info.meeting)) { toast(SITE_VIA_CRM_NEW); return; }
    st.siteBusy = true;
    paintSite();
    try {
      const out = await siteLink(code, fresh);
      st.siteUrl = out.url;
      save(); // the link he sent survives his reload, as the tour does
      if (fresh) toast(out.confirmed ? "A new link — the old one no longer opens the meeting." : "A new link. The old one no longer opens the meeting; whoever held it may take a moment to drop.");
    } catch (err) {
      // RM-hosted, and ClikWork has made no link yet (409 site_via_crm): the box says where the link comes from
      if (isSiteViaCrm(err)) { st.siteUrl = ""; save(); }
      else if (!quiet) toast(err.message || "The link could not be made just now.");
    }
    st.siteBusy = false;
    paintSite();
  }
  function renewLink(anchor) {
    if (siteViaCrm(info.meeting)) { toast(SITE_VIA_CRM_NEW); return; }
    const on = sitePeople();
    if (!on.length) { askLink(true); return; }
    showMenu(anchor, h("div", {}, menuItem(`New link — ${firstName(on[0].name) || "the site camera"} is put out`, () => askLink(true), { danger: true, iconName: "link" })), { label: "New link" });
  }
  function showSite(on, who = null, { muteLaptop = false } = {}) {
    if (on) {
      if (st.shareTrack) { toast("Stop sharing your tab first — one live picture at a time."); return; }
      if (!who || !camOf(who.identity)) { toast("The site camera is not sending a picture yet."); return; }
      st.cam = who.identity;
      st.siteAsked.add(who.identity);
      if (muteLaptop) muteForSite();
      signal(`Showing the site camera · ${firstName(who.name) || "live"}`);
      postEvents(code, [{ t: "site", kind: "on", text: firstName(who.name) || "" }]); // for the lead (ms-signals)
    } else {
      if (!st.cam) return;
      const was = room.remoteParticipants.get(st.cam);
      st.cam = null;
      st.glassOn = null;
      postEvents(code, [{ t: "site", kind: "off", text: firstName(was?.name) || "" }]);
    }
    // Each picture starts on clean glass everywhere, and leaves none behind (as a share does).
    if (book.count("share")) book.clear("share");
    send(TOPIC.INK_OP, { op: "clear", page: "share" });
    st.sz = 1; st.shareDims = null; st.cleared = null;
    saveInk();
    paint(true);
    pushState();
    paintSite();
    logShown();
  }
  // Ravi, 27 Sep (A): the phone and the navigator were joined only by a line of small print. When a site camera's picture
  // arrives and is not on the stage, the stage asks him — once per phone. "Show it and mute this laptop" is for his own
  // phone (Aquon's Go live): his voice then comes from the phone, and two microphones in one room echo. The laptop's
  // microphone comes back when that phone leaves, unless he has touched it since.
  let micTurns = 0;
  async function muteForSite() {
    const lp = room.localParticipant;
    if (!lp.isMicrophoneEnabled) return;
    const turn = ++micTurns; // his own press while this waits is his choice, and wins (re-audit, Low 2)
    try { await lp.setMicrophoneEnabled(false); if (turn === micTurns) st.siteMuted = true; toast("This laptop's microphone is off — your voice comes from the phone."); }
    catch { toast("Mute this laptop's microphone yourself, or they will hear an echo."); }
    paintStatus(); paintTiles();
  }
  async function unmuteAfterSite() {
    if (!st.siteMuted) return;
    st.siteMuted = false;
    try { await room.localParticipant.setMicrophoneEnabled(true); toast("This laptop's microphone is back on."); } catch { toast("Your microphone is blocked."); }
    paintStatus(); paintTiles();
  }
  function paintSiteAsk() {
    const p = st.cam || st.shareTrack ? null : sitePeople().find((x) => !st.siteAsked.has(x.identity) && camOf(x.identity));
    siteAsk.hidden = !p;
    if (!p) { siteAsk.replaceChildren(); return; }
    const cust = firstName(primary()?.name) || "them";
    const name = firstName(p.name) || "The site camera";
    siteAsk.replaceChildren(
      h("p", { class: "k", text: "Site camera · live" }),
      h("p", { class: "t", text: `${name}'s phone is live` }),
      h("p", { class: "s", text: `${cust} cannot see it yet. If it is your own phone, show it and mute this laptop — your voice then comes from the phone.` }),
      h("div", { class: "row" },
        h("button", { class: "btn-s gold", type: "button", text: `Show it to ${cust}`, onclick: () => showSite(true, p) }),
        h("button", { class: "btn-s ghost", type: "button", text: "Show it and mute this laptop", onclick: () => showSite(true, p, { muteLaptop: true }) }),
        h("button", { class: "btn-s ghost", type: "button", text: "Not now", onclick: () => { st.siteAsked.add(p.identity); paintSiteAsk(); } })));
  }
  function paintSite() {
    paintSiteAsk();
    const on = sitePeople();
    const cust = firstName(primary()?.name) || "them";
    const mode = siteBoxMode(info.meeting, { siteUrl: st.siteUrl });
    const linkRow = mode === "shown" ? [
      // RM-hosted: the link ClikWork made through Helix (L3.4) — copy it, never make or replace it here
      h("p", { class: "site-url", text: st.siteUrl.replace(/^https?:\/\//, "") }),
      h("div", { class: "row" },
        h("button", { class: "btn-s ghost", type: "button", text: "Copy", onclick: () => navigator.clipboard?.writeText(st.siteUrl).then(() => toast("Copied"), () => toast("Copy the link above by hand.")) })),
      h("p", { class: "assist", text: SITE_VIA_CRM_NEW })] : mode === "crm" ? [] : st.siteUrl ? [
      h("p", { class: "site-url", text: st.siteUrl.replace(/^https?:\/\//, "") }),
      h("div", { class: "row" },
        h("button", { class: "btn-s ghost", type: "button", text: "Copy", onclick: () => navigator.clipboard?.writeText(st.siteUrl).then(() => toast("Copied"), () => toast("Copy the link above by hand.")) }),
        h("a", { class: "btn-s ghost", href: `https://wa.me/?text=${encodeURIComponent(`Please open this on your phone at the site — you will be the camera for our Walkin meeting: ${st.siteUrl}`)}`, target: "_blank", rel: "noopener", text: "WhatsApp" }),
        h("button", { class: "btn-s ghost", type: "button", text: st.siteBusy ? "Making a link…" : "New link", disabled: st.siteBusy, onclick: (e) => renewLink(e.currentTarget) }))] : [];
    if (on.length) {
      const p = on.find((x) => x.identity === st.cam) || on[0];
      const live = st.cam === p.identity;
      siteBox.replaceChildren(
        h("p", { class: "pen-line", text: `${firstName(p.name) || "Someone"} is on the site camera${camOf(p.identity) ? "" : " — waiting for the picture"}.` }),
        h("div", { class: "row" }, live
          ? h("button", { class: "btn-s ghost", type: "button", text: "Back to the story", onclick: () => showSite(false) })
          : h("button", { class: "btn-s gold", type: "button", text: `Show it to ${cust}`, onclick: () => showSite(true, p) }),
          st.siteUrl || mode !== "make" ? null : h("button", { class: "btn-s ghost", type: "button", text: "The link", onclick: () => askLink(false) })),
        ...linkRow,
        // g-site-privacy (CR-20261009-001 L3.1, RM-hosted only): the site phone hears the room; nothing else of hers
        // reaches it. On today's meeting the site person is usually the sales manager himself: no line.
        rmHosted(info.meeting) ? h("p", { class: "assist", text: "The builder's person on the site camera can hear everyone." }) : null);
      return;
    }
    if (mode !== "make") {
      // RM-hosted (L3.4): no Make a link here, and no Aquon — the link is sent from ClikWork to the builder's site person
      siteBox.replaceChildren(
        h("p", { class: "assist", text: `${SITE_VIA_CRM} Once the builder's site person opens it, this screen asks you to show the picture, and you point and draw on it.` }),
        ...(mode === "shown" ? [h("p", { class: "pen-line", text: "Nobody is on it yet." }), ...linkRow] : []));
      return;
    }
    siteBox.replaceChildren(
      h("p", { class: "assist", text: "At the site yourself: in the Visitin app on your phone, open this meeting and tap Go live. Someone else there: make a link and send it to them. Either way this screen asks you to show the picture, and you point and draw on it." }),
      ...(st.siteUrl ? [h("p", { class: "pen-line", text: "Nobody is on it yet." }), ...linkRow]
        : [h("div", { class: "row" }, h("button", { class: "btn-s gold", type: "button", text: st.siteBusy ? "Making a link…" : "Make a link", disabled: st.siteBusy, onclick: () => askLink(false) }))]));
  }

  // ------------------------------------------------------------------------------------------ kept (W4) --
  // Her finished mark in his words, against the canvas's own names for places (Mana's material) — the mock-up's
  // signalFor: a tick by his note, a circle round a place, a line to a place, a note by a place.
  let marksSaid = 0; // her marks said in words this meeting: a flood from a pen is not a flood of events
  function markWords(page, s, who) {
    const glass = page === "share";
    const [id, variant] = isPage(page) || glass ? [page] : page.split(":");
    const sc = sceneOf(canvas, id);
    const where = glass ? (st.cam ? "the site camera" : "the shared tab") : sceneName(canvas, id) + (sc.kind === "plan" && variant ? ` · ${variantOf(sc, { variant }).name}` : "");
    const spots = isPage(page) || glass ? [] : spotsOf(sc, { variant });
    const pct = (p) => [p[0] / 100, p[1] / 100];
    const dist = (a, b) => Math.hypot(a[0] - b[0], a[1] - b[1]);
    const near = (p, within) => spotNear(spots, p[0], p[1], within);
    if (s.tool === "label") { const n = near(pct(s.pts[0]), 16); return `${who} wrote “${s.text}”${n ? ` by ${n}` : ` on ${where}`}`; }
    const P = s.pts.map(pct), xs = P.map((p) => p[0]), ys = P.map((p) => p[1]);
    const c = [(Math.min(...xs) + Math.max(...xs)) / 2, (Math.min(...ys) + Math.max(...ys)) / 2];
    const size = Math.max(Math.max(...xs) - Math.min(...xs), Math.max(...ys) - Math.min(...ys));
    const note = size < 9 && book.get(page).find((x) => x.tool === "label" && roleOf({ identity: x.by }) === "host" && dist(pct(x.pts[0]), c) < 18);
    if (note) return `${who} ticked your note “${note.text}”`;
    const closed = P.length > 8 && dist(P[0], P[P.length - 1]) < Math.max(6, size * 0.35);
    if (closed) { const n = near(c, 14); return n ? `${who} circled ${n}` : `${who} circled something on ${where}`; }
    const n = near(P[P.length - 1], 10);
    return n ? `${who} drew a line to ${n}` : `${who} drew on ${where}`;
  }
  // As he starts: the studio's book is read before anything is written, and merged with this tab's three ways. The
  // common ground is the studio's own book when this tab is as new as it (a reload), and otherwise what this tab held
  // as it started — so a new tab or another laptop takes the studio's, a tab left behind while he drew elsewhere takes the
  // studio's, and a mark drawn here before the read came back is kept, not wiped (W4 audit P1). Stills are made whole
  // both ways. Offline, this tab's copy stands and the read is tried again.
  async function restoreKept() {
    if (stopped) return;
    let got;
    try { got = await readKept(code); } catch { if (!stopped) setTimeout(restoreKept, 10_000); return; }
    if (stopped) return;
    const theirs = new Map(Object.entries(got.pages || {}).map(([p, l]) => [p, JSON.stringify(l)]));
    mergeInto(got, hadLocal && got.rev <= rev ? theirs : startBook);
    const theirStills = new Set((got.stills || []).map((x) => x.n));
    for (const x of got.stills || []) {
      if (canvas.stills.has(x.n) || x.n > LIMITS.stills) continue;
      try {
        const blob = await readStill(code, x.n);
        canvas.stills.set(x.n, { src: URL.createObjectURL(blob), w: x.w, h: x.h, file: new File([blob], `still-${x.n}-${x.w}x${x.h}.jpg`, { type: "image/jpeg" }) });
        st.stillsN = Math.max(st.stillsN || 0, x.n);
        // a still is the floor plan's: never to the site phone on an RM-hosted meeting (g-site-privacy, L3.1)
        for (const p of room.remoteParticipants.values()) {
          if (!stillGoesTo(p.identity, info.meeting)) continue;
          room.localParticipant.sendFile(canvas.stills.get(x.n).file, { topic: TOPIC.STILL, destinationIdentities: [p.identity] }).catch(() => {});
        }
      } catch { /* the next start tries again */ }
    }
    if (stopped) return;
    let gap = 0; // a still at a time, not a burst into the rate limit (re-audit round three)
    for (const [n, x] of canvas.stills) if (!theirStills.has(n) && x.file) keepStillSoon(n, x, (gap += 1500));
    restored = true;
    keepStills();
    save();
    paint(true);
    inkChanged();
    sendInk({ snapshot: book.snapshot() }); // every phone gets the book he now holds
  }
  // A still is kept in the studio until it is (W4 audit P2): tried again, later each time, while this navigator lives.
  function keepStillSoon(n, x, wait = 0) {
    if (stopped) return;
    setTimeout(() => keepStill(code, n, x.w, x.h, x.file).catch((err) => {
      // Refused for good (too large, not a JPEG): said once, never retried for the rest of the meeting (re-audit N4).
      if (err.status >= 400 && err.status < 500 && !WAIT.has(err.status)) { toast("A still could not be kept in the studio — it stays in this meeting.", 6000); return; }
      keepStillSoon(n, x, Math.min(60_000, (wait || 2500) * 2));
    }), wait);
  }
  // The next still's number: past every still this tab has, and every still page a mark is on — a still whose picture
  // never reached the studio must not lend its number, and its marks, to the next one (W4 audit P2).
  const nextStill = () => Math.max(st.stillsN || 0, ...canvas.stills.keys(), ...[...book.pages.keys()].filter(isStill).map((k) => +k.split(":")[1]), 0) + 1;

  // A stroke from someone with a pen: drawn as it arrives, kept in his book (the truth for anyone joining later).
  function onOthersLive(body, p) {
    if (typeof body.id !== "string" || !pageOk(canvas, body.page)) return;
    const prev = st.others.get(body.id);
    if (prev && prev.by !== p.identity) return;
    if (body.cancel) { st.others.delete(body.id); paintHostInk(); return; }
    if (!prev && st.others.size >= 24) return; // strokes in progress are few; a flood is not kept (re-audit F4a)
    const s = cleanStroke({ id: body.id, tool: body.tool, pts: [...(prev ? prev.pts : []), ...(Array.isArray(body.pts) ? body.pts : [])].slice(0, LIMITS.points), by: p.identity });
    if (!s || !["pen", "hl"].includes(s.tool)) return;
    st.others.set(body.id, { ...s, page: body.page, at: Date.now() });
    setTimeout(() => { const l = st.others.get(body.id); if (l && Date.now() - l.at > 4000) { st.others.delete(body.id); paintHostInk(); } }, 4200);
    paintHostInk();
  }
  function onOthersOp(body, p) {
    if (body.op !== "del" || !pageOk(canvas, body.page)) return;
    const ids = allowedDeletes(book.get(body.page), body.ids, p.identity, false);
    if (ids.length) { book.remove(body.page, ids); inkChanged(); }
  }
  function paintInkTools() {
    const page = curPage();
    inkTools.hidden = !page;
    navCanvas.classList.toggle("inking", Boolean(page) && st.tool !== "pointer");
    if (!page) return;
    const n = book.count(page);
    inkTools.replaceChildren(h("span", { class: "k", text: "Ink" }),
      ...INK_TOOLS.map(([k, label, key]) => tool(label, st.tool === k, () => pickTool(k), { title: `${label.replace(/^\W+ /, "")} (${key})`, "data-ink": k })),
      h("span", { class: "sep" }),
      tool("↶ Undo", false, () => undo(), { title: "Undo your last mark on this page (⌘Z)" }),
      tool("Clear page", false, () => clearPage(), { title: "Take every mark off this page, on every screen" }),
      h("span", { class: "ink-n", text: n ? `${n} mark${n === 1 ? "" : "s"} on this page` : "" }));
  }
  function pickTool(k) {
    st.tool = k;
    if (k !== "pointer" && st.ptr) { st.ptr = null; paintPointer(navCanvas, null); pushPtr(null); }
    paintInkTools();
  }
  function inkChanged() {
    saveInk();
    paintHostInk();
    paintInkTools();
  }
  function commitStroke(page, stroke) {
    const s = book.add(page, { ...stroke, by: me() });
    if (!s) { toast(book.count(page) >= LIMITS.strokes ? "This page is full — clear some marks first." : "That mark could not be kept."); return; }
    st.cleared = null;
    sendInk({ page, stroke: s });
    inkChanged();
  }
  function undo() {
    const page = curPage();
    if (!page) return;
    if (st.cleared && st.cleared.page === page) {
      const back = st.cleared.strokes;
      st.cleared = null;
      for (const s of back) book.add(page, s);
      sendInk({ page, strokes: book.get(page) });
      inkChanged();
      return;
    }
    const last = book.lastBy(page, me());
    if (!last) return;
    book.remove(page, [last.id]);
    send(TOPIC.INK_OP, { op: "del", page, ids: [last.id] });
    inkChanged();
  }
  function clearPage() {
    const page = curPage();
    if (!page || !book.count(page)) return;
    st.cleared = { page, strokes: book.clear(page) };
    send(TOPIC.INK_OP, { op: "clear", page });
    inkChanged();
    toast("Cleared on every screen — Undo brings it back.");
  }
  function erase(page, pt) {
    const s = nearestStroke(book.get(page), pt);
    if (!s) return;
    book.remove(page, [s.id]);
    send(TOPIC.INK_OP, { op: "del", page, ids: [s.id] });
    inkChanged();
  }
  // A label is typed where he clicked, then drawn as text on every screen.
  function askLabel(page, pt) {
    const f = navCanvas.querySelector(".cv-fit");
    if (!f) return;
    const input = h("input", { class: "ink-in", maxlength: String(LIMITS.label), placeholder: "Type a label, then Enter", "aria-label": "A label on the plan", style: `left:${pt[0] / 100}%;top:${pt[1] / 100}%` });
    f.append(input);
    input.focus();
    let done = false;
    const finish = (keep) => {
      if (done) return;
      done = true;
      const text = input.value.trim();
      input.remove();
      if (keep && text && curPage() === page) commitStroke(page, { id: inkId(), tool: "label", pts: [pt], text });
    };
    input.addEventListener("keydown", (e) => { e.stopPropagation(); if (e.key === "Enter") finish(true); if (e.key === "Escape") finish(false); });
    input.addEventListener("blur", () => finish(true));
    input.addEventListener("pointerdown", (e) => e.stopPropagation());
  }
  const pushLive = throttle(() => {
    const l = st.live;
    if (!l || l.tool === "label") return;
    const pts = l.pts.slice(l.sent);
    if (!pts.length) return;
    l.sent = l.pts.length;
    send(TOPIC.INK_LIVE, { id: l.id, page: l.page, tool: l.tool, pts }, { reliable: false });
  }, 60);
  function startStroke(e) {
    const page = curPage();
    if (!page || st.tool === "pointer") return false;
    const pt = unitsAt(navCanvas.querySelector(".cv-fit"), e.clientX, e.clientY);
    if (!pt) return false;
    e.preventDefault();
    if (st.tool === "eraser") { erase(page, pt); return true; }
    if (st.tool === "label") { askLabel(page, pt); return true; }
    navCanvas.setPointerCapture?.(e.pointerId);
    st.live = { id: inkId(), page, tool: st.tool, pts: st.tool === "arrow" ? [pt, pt] : [pt], sent: 0 };
    pushLive();
    paintHostInk();
    return true;
  }
  function moveStroke(e) {
    const l = st.live;
    const pt = unitsAt(navCanvas.querySelector(".cv-fit"), e.clientX, e.clientY, true);
    if (!pt) return;
    if (l.tool === "arrow") { l.pts[1] = pt; l.sent = 0; }
    else {
      const q = l.pts[l.pts.length - 1];
      if (Math.hypot(pt[0] - q[0], pt[1] - q[1]) < 35 || l.pts.length >= LIMITS.points) return;
      l.pts.push(pt);
    }
    pushLive();
    paintHostInk();
  }
  function endStroke() {
    const l = st.live;
    if (!l) return;
    st.live = null;
    if (l.tool === "laser") {
      // A laser mark fades on every screen by itself and is never kept.
      send(TOPIC.INK_LIVE, { id: l.id, page: l.page, tool: "laser", pts: l.pts.slice(l.sent), end: true }, { reliable: false });
      const fading = { ...l, fading: true };
      st.lasers.push(fading);
      setTimeout(() => { st.lasers = st.lasers.filter((x) => x !== fading); paintHostInk(); }, 1700);
      paintHostInk();
      return;
    }
    if (l.tool === "pen" && l.pts.length === 1) l.pts.push([l.pts[0][0] + 10, l.pts[0][1]]);
    commitStroke(l.page, { id: l.id, tool: l.tool, pts: l.pts });
  }

  // The pointer, in the image's own units, and turning in the walk-through by dragging.
  const pushPtr = throttle((p) => send(TOPIC.PTR, p, { reliable: false }), 40);
  let drag = null;
  navCanvas.addEventListener("pointermove", (e) => {
    if (drag) {
      set({ yaw: Math.max(0, Math.min(100, drag.yaw - ((e.clientX - drag.x) / drag.w) * 70)) });
      return;
    }
    if (st.live) { moveStroke(e); return; }
    if (st.tool !== "pointer" && curPage()) return;
    const doc = !onGlass() && navCanvas.querySelector(".cv-doc");
    if (doc) { st.ptr = docAnchor(doc, e.clientX, e.clientY); paintPointer(navCanvas, st.ptr); pushPtr(st.ptr); return; }
    const f = navCanvas.querySelector(".cv-fit");
    if (!f) return;
    const r = f.getBoundingClientRect();
    const x = ((e.clientX - r.left) / r.width) * 100, y = ((e.clientY - r.top) / r.height) * 100;
    st.ptr = x < 0 || x > 100 || y < 0 || y > 100 ? null : { x: Math.round(x * 10) / 10, y: Math.round(y * 10) / 10 };
    paintPointer(navCanvas, st.ptr);
    pushPtr(st.ptr);
  });
  navCanvas.addEventListener("pointerleave", () => { st.ptr = null; paintPointer(navCanvas, null); pushPtr(null); });
  navCanvas.addEventListener("pointerdown", (e) => {
    if (e.button > 0 || e.target.closest("input")) return;
    if (startStroke(e)) return;
    if (!e.target.closest(".pano")) return;
    drag = { x: e.clientX, yaw: st.yaw, w: navCanvas.clientWidth || 1 };
    navCanvas.setPointerCapture?.(e.pointerId);
    e.preventDefault();
  });
  navCanvas.addEventListener("pointerup", () => { drag = null; endStroke(); });
  navCanvas.addEventListener("pointercancel", () => { drag = null; endStroke(); });

  function onKey(e) {
    if (e.target.closest("input, textarea, select")) return;
    if ((e.metaKey || e.ctrlKey) && !e.altKey && e.key.toLowerCase() === "z" && curPage()) { e.preventDefault(); undo(); return; }
    if (e.metaKey || e.ctrlKey || e.altKey) return;
    const k = { p: "pen", l: "laser", h: "hl", a: "arrow", t: "label", e: "eraser", escape: "pointer" }[e.key.toLowerCase()];
    if (k && curPage()) { e.preventDefault(); pickTool(k); return; }
    const i = scenes.findIndex((s) => s.id === st.scene);
    if (e.key === "ArrowRight" && scenes[i + 1]) { e.preventDefault(); go(scenes[i + 1].id); }
    if (e.key === "ArrowLeft" && scenes[i - 1]) { e.preventDefault(); go(scenes[i - 1].id); }
  }
  document.addEventListener("keydown", onKey);

  // ------------------------------------------------------------------------------------------ controls --
  async function toggleMic() {
    st.siteMuted = false; // his own choice from here on: nothing turns it back on for him
    micTurns++;
    try { await room.localParticipant.setMicrophoneEnabled(!room.localParticipant.isMicrophoneEnabled); } catch { toast("Your microphone is blocked."); }
    paintStatus(); paintTiles();
  }
  async function toggleCam() {
    try { await room.localParticipant.setCameraEnabled(!room.localParticipant.isCameraEnabled); } catch { toast("Your camera is blocked."); }
    paintStatus(); paintTiles();
  }
  // ------------------------------------------------------------------------------------------ the shared tab (W3) --
  // His own tab, drawn on his stage at the tab's own size, with an ink layer over it. Marks are on the glass: if the tab
  // scrolls they stay where they were drawn, so a mark worth keeping is frozen with its frame as a still (D92 A).
  const STILLS_SAVED = `ms-stills:${code}`;
  function renderShare() {
    panoIn(navCanvas)?.destroy();
    const old = navCanvas.querySelector("video.share-src");
    if (old && st.glassOn) st.glassOn.detach(old);
    const d = st.shareDims || { w: 1600, h: 1000 };
    navCanvas.innerHTML = `<div class="cv share"><div class="cv-center"><div class="cv-fit" data-w="${d.w}" data-h="${d.h}"><video class="share-src" autoplay playsinline muted></video><svg class="ink" viewBox="0 0 ${d.w} ${d.h}" preserveAspectRatio="none" aria-hidden="true"></svg><div class="ink-lbls"></div><div class="ptr" hidden><span></span></div></div></div></div>`;
    st.key = "share";
    const v = navCanvas.querySelector("video.share-src");
    st.glassOn = glassTrack();
    st.glassOn?.attach(v);
    const sized = () => {
      if (!v.videoWidth || !v.videoHeight) return;
      const next = { w: v.videoWidth, h: v.videoHeight };
      const was = st.shareDims;
      // Only a new SHAPE moves what is under the marks (the marks are in the frame's own units). A camera sends several
      // sizes of the same picture and steps between them with the bandwidth, and a tab can be scaled: those change the
      // pixels, not the picture, and wiped every mark on every screen (audit S1, P1).
      if (was && Math.abs(was.w / was.h - next.w / next.h) > 0.02 && book.count("share")) {
        st.cleared = { page: "share", strokes: book.clear("share") };
        send(TOPIC.INK_OP, { op: "clear", page: "share" });
        toast(st.cam ? "The picture turned, so its marks were cleared — Undo brings them back." : "The tab changed shape, so its marks were cleared — Undo brings them back.");
      }
      st.shareDims = next;
      const f = navCanvas.querySelector(".cv-fit");
      if (!f) return;
      f.dataset.w = next.w; f.dataset.h = next.h;
      f.querySelector(".ink").setAttribute("viewBox", `0 0 ${next.w} ${next.h}`);
      fitPlans(navCanvas);
      applyShareZoom();
      paintHostInk();
    };
    v.addEventListener("loadedmetadata", sized);
    v.addEventListener("resize", sized);
    fitPlans(navCanvas);
    applyShareZoom();
  }
  function applyShareZoom() {
    const f = navCanvas.querySelector(".cv.share .cv-fit");
    if (f) { f.style.transformOrigin = "50% 50%"; f.style.transform = `scale(${st.sz})`; }
  }
  function shareZoom(d) {
    st.sz = d === 0 ? 1 : Math.max(1, Math.min(3, Math.round((st.sz + d) * 10) / 10));
    applyShareZoom();
    pushState();
  }
  async function freeze() {
    const v = navCanvas.querySelector("video.share-src");
    if (!v || !v.videoWidth) { toast(st.cam ? "The site camera's picture has not arrived yet." : "The tab has not appeared yet."); return; }
    if (!restored) { toast("One moment — the stills kept from this meeting are still loading."); return; }
    if (canvas.stills.size >= 20) { toast("Twenty stills is the most in one meeting."); return; }
    const k = Math.min(1, 1600 / v.videoWidth);
    const w = Math.round(v.videoWidth * k), h2 = Math.round(v.videoHeight * k);
    const c = document.createElement("canvas");
    c.width = w; c.height = h2;
    c.getContext("2d").drawImage(v, 0, 0, w, h2);
    const blob = await new Promise((r) => c.toBlob(r, "image/jpeg", 0.82));
    if (!blob) { toast("That frame could not be kept."); return; }
    const n = nextStill();
    if (n > LIMITS.stills) { toast("Twenty stills is the most in one meeting."); return; }
    st.stillsN = n;
    save();
    const file = new File([blob], `still-${n}-${w}x${h2}.jpg`, { type: "image/jpeg" });
    canvas.stills.set(n, { src: URL.createObjectURL(blob), w, h: h2, file });
    // The glass's marks move onto the still, where they stay put.
    const id = `still:${n}`;
    for (const s of book.get("share")) book.add(id, s);
    book.clear("share");
    if (!rmHosted(info.meeting)) {
      try { await room.localParticipant.sendFile(file, { topic: TOPIC.STILL }); } catch { toast("The still could not be sent — it is kept on your screen."); }
    } else {
      // RM-hosted (g-site-privacy, CR-20261009-001 L3.1): to each screen but the site phone's — a broadcast would reach
      // it too. One screen's failure never skips the rest (L3 audit P3-5).
      let failed = false;
      for (const p of room.remoteParticipants.values()) {
        if (isSite(p.identity)) continue;
        try { await room.localParticipant.sendFile(file, { topic: TOPIC.STILL, destinationIdentities: [p.identity] }); } catch { failed = true; }
      }
      if (failed) toast("The still did not reach everyone — it is kept on your screen.");
    }
    keepStillSoon(n, canvas.stills.get(n)); // in the studio too (W4), until it is; the tab keeps it meanwhile
    sendInk({ page: id, strokes: book.get(id) });
    send(TOPIC.INK_OP, { op: "clear", page: "share" });
    keepStills();
    inkChanged();
    paintRail();
    toast("Frozen as a page — it is in your story under Boards and stills.");
  }
  // Stills survive his reload in this tab, as the ink does (a nicety: a full store is W4's).
  async function keepStills() {
    try {
      const all = await Promise.all([...canvas.stills].map(async ([n, x]) => ({ n, w: x.w, h: x.h, data: await new Promise((r) => { const fr = new FileReader(); fr.onload = () => r(fr.result); fr.readAsDataURL(x.file); }) })));
      sessionStorage.setItem(STILLS_SAVED, JSON.stringify(all));
    } catch { try { sessionStorage.removeItem(STILLS_SAVED); } catch { /* nothing to free */ } } // the ink needs the room more
  }
  (() => {
    try {
      for (const x of JSON.parse(sessionStorage.getItem(STILLS_SAVED) || "[]")) {
        // Decoded here, not fetched: the page's connect-src does not allow data: URLs.
        const bin = atob(String(x.data).split(",")[1] || "");
        const blob = new Blob([Uint8Array.from(bin, (c) => c.charCodeAt(0))], { type: "image/jpeg" });
        canvas.stills.set(x.n, { src: URL.createObjectURL(blob), w: x.w, h: x.h, file: new File([blob], `still-${x.n}-${x.w}x${x.h}.jpg`, { type: "image/jpeg" }) });
      }
      if (canvas.stills.size) paint(true);
    } catch { /* nothing kept, or a private window */ }
  })();
  function sendStills(to) {
    if (!stillGoesTo(to, info.meeting)) return;
    for (const x of canvas.stills.values()) room.localParticipant.sendFile(x.file, { topic: TOPIC.STILL, destinationIdentities: [to] }).catch(() => {});
  }

  // A tab share is the fallback for something the canvas does not have: one browser tab, never a window or the screen.
  async function toggleShare() {
    if (st.cam) { toast("Go back to the story first — the site camera is on the stage."); return; }
    if (st.shareTrack) {
      const t0 = st.shareTrack;
      st.shareTrack = null;
      try { await room.localParticipant.unpublishTrack(t0); } catch { /* gone */ }
      t0.stop();
      // Marks on the glass go with the tab; a frozen still keeps its own.
      if (book.count("share")) { book.clear("share"); send(TOPIC.INK_OP, { op: "clear", page: "share" }); }
      st.sz = 1; st.shareDims = null; st.cleared = null;
      saveInk();
      paint(true);
      pushState();
      logShown();
      return;
    }
    let mst;
    try { mst = (await pickShare(SHARE_TAB)).video; } catch (err) { if (err.refused) toast(err.message, 6000); return; }
    if ("contentHint" in mst) mst.contentHint = "detail";
    const track = new LocalVideoTrack(mst, undefined, true);
    await room.localParticipant.publishTrack(track, { source: Track.Source.ScreenShare, simulcast: false, videoEncoding: ScreenSharePresets.h1080fps15.encoding });
    st.shareTrack = track;
    paintSiteAsk(); // the room's own event came before shareTrack was set: no ask left over the tab (re-audit, Low 1)
    mst.addEventListener("ended", () => { if (st.shareTrack === track) toggleShare(); });
    st.sz = 1;
    // A new share starts on clean glass everywhere — a phone may still hold marks from a share that dropped (audit, P1).
    book.clear("share");
    send(TOPIC.INK_OP, { op: "clear", page: "share" });
    saveInk();
    paint(true);
    pushState();
    logShown();
  }
  function endMenu() {
    showMenu(endBtn, h("div", {},
      menuItem("End the meeting for everyone", () => moderate(code, "end").catch((err) => toast(err.message)), { danger: true, iconName: "leave" }),
      menuItem("Leave, and let them stay", () => session.leave(), { iconName: "leave" })), { label: "End" });
  }

  // ------------------------------------------------------------------------------------------ messages --
  function onData(payload, participant, _kind, topic) {
    if (!participant || roleOf(participant) === "host") return;
    if (topic === TOPIC.PEER_PTR) { onPeerPointer(decode(payload), participant); return; }
    const body = decode(payload);
    if (!body) return;
    if (topic === TOPIC.INK_LIVE || topic === TOPIC.INK_OP) {
      if (!mayDraw(participant, st.holders)) return;
      if (topic === TOPIC.INK_LIVE) onOthersLive(body, participant);
      else onOthersOp(body, participant);
      return;
    }
    if (topic === TOPIC.STATUS) {
      account();
      const known = st.cust.has(participant.identity);
      const prev = st.cust.get(participant.identity);
      st.cust.set(participant.identity, { ...body, since: Date.now() });
      // A screen reports its status only once its handlers are listening (and again when he rejoins), so this — not its
      // arrival — is when it gets the book, the pens and the stills; sent sooner they were dropped on a slow phone (audit, P1).
      if (!known) {
        send(TOPIC.STATE, view(), { to: participant.identity });
        sendInk({ snapshot: book.snapshot() }, participant.identity);
        send(TOPIC.PEN, { holders: [...st.holders] }, { to: participant.identity });
        sendStills(participant.identity);
      }
      if (prev && prev.follow && !body.follow) signal(`${firstName(participant.name)} is exploring on their own`);
      if (prev && !prev.face && body.face) signal(`${firstName(participant.name)} put you full screen`);
      paintStatus();
      paintTools(sceneOf(canvas, st.scene));
    } else if (topic === TOPIC.REQ) {
      const name = firstName(participant.name);
      if (body.kind === "sig") { signal(String(body.text || "").slice(0, 120)); return; }
      if (body.kind === "pen-done") { if (st.holders.has(participant.identity)) setHolders([...st.holders].filter((i) => i !== participant.identity)); return; }
      const r = { id: String(body.id || crypto.randomUUID()), from: participant.identity, kind: body.kind, unit: body.unit, text: body.text, fresh: true };
      if (body.kind === "cost") { r.title = `${name} asked for the cost sheet`; r.line = String(body.unit || ""); }
      else if (body.kind === "ask") { r.title = `${name} asks: ${String(body.text || "").slice(0, 200)}`; }
      else if (body.kind === "book") { r.title = `${name} booked a site visit`; r.line = `${body.day}, ${body.time}${body.pickup ? " · pickup from home" : ""}`; r.done = "Booked"; }
      else return;
      st.requests.push(r);
      save();
      setTimeout(() => { r.fresh = false; }, 1600);
      toast(r.title);
      paintRequests();
    }
  }
  const relayout = () => { paintTiles(); paintStatus(); paintSite(); if (st.cam) paint(); };
  // A finished stroke from someone with a pen. Only a stroke or a page's strokes — a whole book comes from him alone.
  room.registerTextStreamHandler(TOPIC.INK, async (reader, from) => {
    const who = { identity: from?.identity };
    if (!who.identity || roleOf(who) === "host" || !mayDraw(who, st.holders)) return;
    if ((reader.info?.size || 0) > LIMITS.stream) return; // a stroke is small; anything larger is not read (audit, P1)
    let text;
    try { text = await readCapped(reader, LIMITS.stream); } catch { return; } // a stream with no declared size too (F4c)
    if (text == null) return;
    let body;
    try { body = JSON.parse(text); } catch { return; }
    if (!body || !pageOk(canvas, body.page) || !body.stroke) return;
    if (!mayDraw(who, st.holders)) return; // the pen may have gone back while the stream was arriving
    const fresh = !book.get(body.page).some((x) => x.id === body.stroke?.id); // a replay of a mark is not a new mark
    const added = book.add(body.page, { ...body.stroke, by: who.identity });
    if (added) {
      if (st.others.get(body.stroke.id)?.by === who.identity) st.others.delete(body.stroke.id);
      inkChanged();
      // Her mark in his words, against the canvas's own names for places (W4): in his panel, and to the lead.
      if (fresh && marksSaid < 300) {
        marksSaid += 1;
        const words = markWords(body.page, added, firstName(room.remoteParticipants.get(who.identity)?.name) || "They");
        signal(words);
        postEvents(code, [{ t: "mark", text: words, scene: body.page }]);
      }
    }
  });
  room
    .on(RoomEvent.DataReceived, onData)
    .on(RoomEvent.ParticipantConnected, (p) => {
      toast(`${p.name || "Someone"} joined`);
      setTimeout(() => { send(TOPIC.STATE, view(), { to: p.identity }); send(TOPIC.PEN, { holders: [...st.holders] }, { to: p.identity }); }, 400);
      relayout(); paint(true); paintPen();
    })
    .on(RoomEvent.ParticipantDisconnected, (p) => {
      toast(`${p.name || "Someone"} left`);
      st.cust.delete(p.identity);
      st.peers.delete(p.identity);
      for (const [id, l] of st.others) if (l.by === p.identity) st.others.delete(id);
      if (st.holders.has(p.identity)) setHolders([...st.holders].filter((i) => i !== p.identity));
      // The site camera dropped while on the stage: every screen goes back to the story, and he can show it again.
      if (p.identity === st.cam) { showSite(false); toast(`${firstName(p.name) || "The site camera"} dropped — back to the story.`, 5000); }
      if (roleOf(p) === "site") { st.siteAsked.delete(p.identity); if (!sitePeople().length) unmuteAfterSite(); }
      relayout(); paintPen(); paintHostInk();
    })
    .on(RoomEvent.TrackSubscribed, relayout)
    .on(RoomEvent.TrackUnsubscribed, relayout)
    .on(RoomEvent.TrackUnpublished, relayout)
    .on(RoomEvent.TrackMuted, relayout)
    .on(RoomEvent.TrackUnmuted, relayout)
    .on(RoomEvent.LocalTrackPublished, relayout)
    .on(RoomEvent.LocalTrackUnpublished, relayout)
    .on(RoomEvent.ActiveSpeakersChanged, paintTiles);
  // Live assist is the room's only hidden participant: the studio's assist pass is the only hidden one it issues.
  // LiveKit stamps every message with its sender's identity and blanks it only for a hidden sender, so a message
  // that names a sender came from someone in the meeting, not from the assist, and is dropped.
  if (assist) {
    room.registerTextStreamHandler(ASSIST_TOPIC, async (reader, from) => {
      if (from?.identity) return;
      try { assist.receive(JSON.parse(await reader.readAll())); } catch { /* a garbled message is dropped */ }
    });
  }

  // Where the customer's attention goes, counted here from what their screen reports.
  let acc = null;
  function account() {
    const now = performance.now();
    if (acc && acc.scene) st.dwell[acc.scene] = (st.dwell[acc.scene] || 0) + (now - acc.since) / 1000;
    const cust = primary();
    const cs = cust && st.cust.get(cust.identity);
    acc = { scene: cs && !cs.face ? (cs.follow ? st.scene : cs.scene) : null, since: now };
  }
  const tick = setInterval(() => {
    timer.textContent = clock((Date.now() - st.startedAt) / 1000);
    account();
    if (Date.now() % 5000 < 1000) { paintSignals(); paintRail(); }
  }, 1000);
  // The site camera cannot report a status, so nothing else would tell it who holds a pen if the one message at its
  // join landed before it was listening: it hears the pens again with every heartbeat (audit S1, P2).
  // The tab closing: the last moment's marks go on a request the browser finishes after the page is gone.
  const onPageHide = () => { if (restored && !stopped) storeInk(true); };
  window.addEventListener("pagehide", onPageHide);
  const heartbeat = setInterval(() => {
    send(TOPIC.STATE, view());
    const sites = sitePeople().map((p) => p.identity);
    if (sites.length) send(TOPIC.PEN, { holders: [...st.holders] }, { to: sites });
  }, 5000);
  const onResize = () => { fitPlans(navCanvas); paint(); };
  window.addEventListener("resize", onResize);

  paint(true);
  logShown(); // the scene he opens on, so the recording's log starts with what was already showing
  paintTiles();
  paintRequests();
  paintSignals();
  send(TOPIC.STATE, view());
  send(TOPIC.PEN, { holders: [] });
  // Phones already here report their status as he rejoins, possibly before his listeners exist; asked again, each one
  // answers now, and its first status brings it the pens and the stills (re-audit N4).
  setTimeout(() => send(TOPIC.ASK, { ask: "status" }), 800);
  // After his reload, phones already in the room report their status as he rejoins — possibly before his listeners
  // exist — so his book goes to everyone once as he starts, as well as to each screen on its first status.
  if (Object.keys(book.snapshot()).length) setTimeout(() => sendInk({ snapshot: book.snapshot() }), 600);
  paintPen();
  paintSite();
  // RM-hosted (L3.4): read the link ClikWork may already have made through Helix — a read, never a make
  if (siteViaCrm(info.meeting)) askLink(false, { quiet: true });
  restoreKept();
  session.ended.then(() => {
    // What he drew in the last moment is written once more — from this tab's rev, so if another laptop has written since,
    // the studio refuses it — then this navigator writes nothing: after a rejoin in this page, or on another laptop, the
    // next one decides from the studio's book (W4).
    stopped = true;
    window.removeEventListener("pagehide", onPageHide);
    if (restored) storeInk(true);
    account();
    clearInterval(tick);
    clearInterval(heartbeat);
    document.removeEventListener("keydown", onKey);
    window.removeEventListener("resize", onResize);
    st.shareTrack?.stop();
    if (assist) { room.unregisterTextStreamHandler(ASSIST_TOPIC); assist.destroy(); }
    room.unregisterTextStreamHandler(TOPIC.INK);
    for (const x of canvas.stills.values()) URL.revokeObjectURL(x.src);
    canvas.stills.clear();
    document.body.classList.remove("ms-dark");
  });
  // What the navigator measured, for the summary if the customer's last report has not landed yet.
  return { dwell: () => ({ ...st.dwell }) };
}
